'use client'
import {
	Conversation,
	ConversationContent,
	ConversationScrollButton,
	ConversationScrollHandle,
	type ConversationScrollHandleRef,
} from '@/components/ai-elements/conversation'
import { Loader } from '@/components/ai-elements/loader'
import {
	PromptInput,
	PromptInputBody,
	PromptInputFooter,
	PromptInputHeader,
	type PromptInputMessage,
	PromptInputSubmit,
	PromptInputTextarea,
} from '@/components/ai-elements/prompt-input'
import { Shimmer } from '@/components/ai-elements/shimmer'
import { Suggestion, Suggestions } from '@/components/ai-elements/suggestion'
import { Button } from '@/components/ui/button'
import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from '@/components/ui/dialog'
import { toolSpecs } from '@/data'
import { useChatEditor } from '@/hooks'
import { useJobillaOrgId } from '@/hooks'
import { logger } from '@/ui'
import { Jobibot } from '@/ui/rapid-ui'
import { isStaticToolUIPart, UIMessage } from 'ai'
import { TFunction } from 'i18next'
import { Eraser } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChatMessage } from './chat-message'

export type EditChatPanelProps = {
	campaignId: string
}

const buildPlaceholderMessages = (t: TFunction): UIMessage[] => [{
	id: 'placeholder-1',
	role: 'assistant',
	parts: [{
		type: 'text',
		text: [
			t('editor.placeholders.allDone'),
			t('editor.placeholders.brandGuidance'),
			t('editor.placeholders.continuePrompt'),
		].join('\n\n'),
	}],
}]

/**
 * Hide redundant assistant messages that are immediately followed by another
 * assistant message (the continuation). The continuation already carries the
 * tool result context, so showing both creates a visual duplicate.
 *
 * Two cases:
 * 1. Tool-only message (no text) followed by assistant → always hide
 * 2. Message with text + tools, followed by assistant that has the same tools → hide (superset)
 */
const collapseRedundantToolMessages = (messages: UIMessage[]): UIMessage[] => {
	return messages.filter((message, index) => {
		if (message.role !== 'assistant') { return true }

		const next = messages[index + 1]
		if (!next || next.role !== 'assistant') { return true }

		// Collect toolCallIds from this message (identity-based, not type-based)
		const toolCallIds = new Set(
			message.parts
				.filter(isStaticToolUIPart)
				.filter(part => part.type !== 'tool-result')
				.map(part => part.toolCallId),
		)

		// No tools in this message — keep it
		if (toolCallIds.size === 0) { return true }

		// Check if the next assistant message contains all the same tool call IDs
		const nextToolCallIds = new Set(
			next.parts
				.filter(isStaticToolUIPart)
				.filter(part => part.type !== 'tool-result')
				.map(part => part.toolCallId),
		)

		const nextHasAllCalls = [...toolCallIds].every(id => nextToolCallIds.has(id))
		if (nextHasAllCalls) { return false } // Next message is a superset — hide this one

		return true
	})
}

const toArchivedTextMessages = (messages: UIMessage[]): UIMessage[] => {
	return messages.flatMap((message) => {
		const archivedParts = message.parts.filter((part) => part.type === 'text' || part.type === 'reasoning')
		if (archivedParts.length === 0) {
			return []
		}

		return [{
			...message,
			parts: archivedParts,
		}]
	})
}

export const EditChatPanel = ({ campaignId }: EditChatPanelProps) => {
	const { t } = useTranslation('common')
	const {
		chat: {
			messages,
			sendMessage,
			status,
			regenerate,
			stop,
			resetChat,
			errorMessage,
			isLoading,
			hasPendingToolCalls,
			chatId,
		},
		isLoadingChatHistory,
	} = useChatEditor({
		campaignId,
	})
	const jobillaOrgId = useJobillaOrgId()
	const scrollHandleRef = useRef<ConversationScrollHandleRef>(null)
	const [isResetDialogOpen, setIsResetDialogOpen] = useState(false)

	useEffect(() => {
		logger.track('Chat: Opened', {
			data: {
				organisation_id: jobillaOrgId,
				campaign_id: campaignId,
				chat_id: chatId,
			},
		})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])
	const [isResetting, setIsResetting] = useState(false)
	const [preResetMessages, setPreResetMessages] = useState<UIMessage[]>([])
	const [hasInput, setHasInput] = useState(false)

	const scrollToBottom = useCallback(() => {
		setTimeout(() => {
			scrollHandleRef.current?.scrollToBottom()
		}, 100)
	}, [])

	const handleStop = useCallback(() => {
		logger.track('Chat: Generation stopped', {
			data: {
				organisation_id: jobillaOrgId,
				campaign_id: campaignId,
				chat_id: chatId,
				message_count: messages.length,
			},
		})
		stop()
	}, [stop, jobillaOrgId, campaignId, chatId, messages.length])

	const isProcessing = useMemo(() => ['submitted', 'streaming'].includes(status), [status])
	const isDisabled = isProcessing || hasPendingToolCalls
	const visibleMessages = useMemo(() => collapseRedundantToolMessages(messages), [messages])

	const handleSubmit = useCallback((message: PromptInputMessage) => {
		const hasText = Boolean(message.text)
		const hasAttachments = Boolean(message.files?.length)
		if (!(hasText || hasAttachments)) {
			return
		}

		sendMessage({
			text: message.text || 'Sent with attachments',
			files: message.files,
		})
		setHasInput(false)
		scrollToBottom()
	}, [sendMessage, scrollToBottom])

	const handleResetConfirm = useCallback(async () => {
		setIsResetting(true)
		// Snapshot messages BEFORE resetChat() clears them.
		const archivedMessages = toArchivedTextMessages(messages)
		try {
			await resetChat()

			if (archivedMessages.length > 0) {
				setPreResetMessages((prev) => {
					const seenIds = new Set(prev.map(message => message.id))
					const next = [...prev]

					archivedMessages.forEach((message) => {
						if (seenIds.has(message.id)) {
							return
						}

						seenIds.add(message.id)
						next.push(message)
					})

					return next
				})
			}
		} catch (error) {
			logger.error(error)
		} finally {
			setIsResetDialogOpen(false)
			setIsResetting(false)
		}
	}, [resetChat, messages])

	const suggestions = useMemo(() => [
		t('editor.chat.suggestCopyTexts'),
		t('editor.chat.addHumour'),
	], [t])

	const placeholderMessages = useMemo(() => buildPlaceholderMessages(t), [t])

	const handleSuggestionClick = useCallback((suggestion: string) => {
		logger.track('Chat: Suggestion clicked', {
			data: {
				suggestion,
				organisation_id: jobillaOrgId,
				chat_id: chatId,
				campaign_id: campaignId,
			},
		})
		handleSubmit({ text: suggestion, files: [] })
	}, [handleSubmit, chatId, campaignId, jobillaOrgId])

	if (isLoadingChatHistory) {
		return (
			<div className="flex flex-col w-full h-full gap-4">
				<div className="flex flex-col flex-1 min-h-0 items-center justify-center">
					<Loader />
					<p className="mt-2 text-sm text-muted-foreground">{t('editor.chat.loadingHistory')}</p>
				</div>
			</div>
		)
	}

	return (
		<div className="flex flex-col w-full h-full">
			{/* Chat messages container - scrollable */}
			<div className="flex-1 min-h-0 overflow-y-auto">
				<Conversation className="h-full">
					<ConversationScrollHandle ref={scrollHandleRef} />
					<ConversationContent className="pl-0">
						{/* Keep the static placeholder messages at the top */}
						{placeholderMessages.map((message, index) => (
							<ChatMessage
								key={message.id}
								message={message}
								toolSpecs={toolSpecs}
								isLastMessage={index === placeholderMessages.length - 1 && messages.length === 0}
								isStreaming={false}
								chatId={chatId || ''}
							/>
						))}

						{/* Pre-reset messages (read-only history, text-only) */}
						{preResetMessages.map((message) => (
							<ChatMessage
								key={message.id}
								message={message}
								toolSpecs={toolSpecs}
								isLastMessage={false}
								isStreaming={false}
								chatId={chatId || ''}
							/>
						))}

						{/* New chat divider */}
						{preResetMessages.length > 0 && (
							<div className="flex items-center gap-3 py-4">
								<div className="flex-1 border-t border-dashed border-muted-foreground/30" />
								<span className="flex items-center gap-2 text-xs text-muted-foreground">
									<Jobibot size={14} />
									{t('editor.chat.newChatStarted')}
								</span>
								<div className="flex-1 border-t border-dashed border-muted-foreground/30" />
							</div>
						)}

						{/* Actual chat messages */}
						{visibleMessages.map((message, index) => (
							<ChatMessage
								key={message.id}
								message={message}
								toolSpecs={toolSpecs}
								isLastMessage={index === visibleMessages.length - 1}
								isStreaming={status === 'streaming'}
								chatId={chatId}
							/>
						))}
						{errorMessage && visibleMessages.length > 0
							&& visibleMessages[visibleMessages.length - 1].role === 'user' && (
							<div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
								{errorMessage}
							</div>
						)}
						{isLoading && (
							<div className="flex text-sm">
								<Loader /> <Shimmer className="ml-2">{t('editor.chat.thinking')}</Shimmer>
							</div>
						)}
					</ConversationContent>
					<ConversationScrollButton />
				</Conversation>
			</div>

			{/* Chat prompt widget - pinned to bottom */}
			<div className="shrink-0 pt-4">
				<PromptInput
					onSubmit={handleSubmit}
					globalDrop={false}
					multiple={false}
					maxFiles={0}
				>
					<PromptInputHeader className="px-0 flex-nowrap items-center gap-2">
						<Suggestions className="flex-1 min-w-0">
							{suggestions.map((suggestion) => (
								<Suggestion
									key={suggestion}
									onClick={handleSuggestionClick}
									suggestion={suggestion}
									className="cursor-pointer rounded-full px-3 text-xs"
								/>
							))}
						</Suggestions>
						<Dialog open={isResetDialogOpen} onOpenChange={setIsResetDialogOpen}>
							<DialogTrigger asChild>
								<Button
									type="button"
									size="sm"
									disabled={messages.length === 0}
									className="cursor-pointer rounded-full px-3 text-xs bg-linear-to-br from-indigo-700 via-indigo-600 via-40% to-brand-600 text-white border-0 shrink-0 shadow-brand-500/30 disabled:opacity-40 disabled:cursor-not-allowed"
								>
									<Eraser className="w-3.5 h-3.5" />
									{t('editor.chat.reset')}
								</Button>
							</DialogTrigger>
							<DialogContent>
								<DialogHeader>
									<DialogTitle>{t('editor.chat.resetDialogTitle')}</DialogTitle>
									<DialogDescription>
										{t('editor.chat.resetDialogDescription')}
									</DialogDescription>
								</DialogHeader>
								<DialogFooter>
									<DialogClose asChild>
										<Button variant="outline" disabled={isResetting}>{t('common.cancel')}</Button>
									</DialogClose>
									<Button
										variant="destructive"
										onClick={handleResetConfirm}
										disabled={isResetting}
									>
										{isResetting ? t('editor.chat.resetting') : t('editor.chat.reset')}
									</Button>
								</DialogFooter>
							</DialogContent>
						</Dialog>
					</PromptInputHeader>
					<PromptInputBody className="flex flex-row w-full rounded-full border border-input p-2 transition-[border-color,box-shadow] has-[textarea:focus-visible]:border-ring">
						<PromptInputTextarea
							disabled={isDisabled}
							className="w-full p-0 h-8 min-h-8 pt-1 pl-2"
							placeholder={t('editor.chat.placeholder')}
							onChange={(e) => setHasInput(e.currentTarget.value.trim().length > 0)}
						/>
						<PromptInputSubmit
							disabled={isDisabled || !hasInput}
							status={status}
							onStop={handleStop}
							className="opacity-80 hover:opacity-100 cursor-pointer rounded-full ml-2 bg-linear-to-br from-indigo-700 via-indigo-600 via-40% to-brand-600 z-10 shadow-brand-500/30 text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
						/>
					</PromptInputBody>
					<p className="text-[10px] text-muted-foreground/60 text-center mt-1">
						{t('editor.chat.inputHint')}
					</p>
					<PromptInputFooter />
				</PromptInput>
			</div>
		</div>
	)
}
